import { Logger } from '_common/services';
import { NodeUtils } from 'Editor/services/DataManager';
import { JsonRange, SelectionFixer } from 'Editor/services/_Common/Selection';
import { StylesUtils } from 'Editor/services/Edition_v2/Managers/Styles/StylesUtils';
import { Command } from '../../Command';

export class ApplyStylesCommand extends Command {
  private stylesToApply: Editor.Edition.StylesMap;
  private options: Editor.Edition.StylesCommandOptions;

  private rangeToApply?: Editor.Selection.JsonRange;

  constructor(
    context: Editor.Edition.Context,
    stylesToApply: Editor.Edition.StylesMap = {},
    rangeToApply?: Editor.Selection.JsonRange,
    options: Editor.Edition.StylesCommandOptions = {},
  ) {
    super(context);

    this.stylesToApply = stylesToApply;
    this.options = {
      applySelection: true,
      createPatch: true,
      ...options,
    };

    this.rangeToApply = rangeToApply;
  }

  private async applyStyleToBlockData(
    baseModel: Editor.Data.Node.Model,
    blockData: Editor.Data.Node.Data,
    blockPath: Editor.Selection.Path,
    style: Editor.Edition.Styles,
  ) {
    if (!this.context.stylesManager || !this.context.contentManipulator) {
      return false;
    }

    if (StylesUtils.isAllowedBlockStyle(blockData.type, style)) {
      if (StylesUtils.isInlineExlusiveStyle(style)) {
        // remove exclusive styles
        await this.context.contentManipulator.removeStyleFromBlock(baseModel, blockPath, style);
      }

      if (StylesUtils.isInlineSingleStateStyle(style)) {
        // remove style
        await this.context.contentManipulator.removeStyleFromBlock(baseModel, blockPath, style);

        if (this.stylesToApply[style] === true) {
          // add style
          await this.context.contentManipulator.applyStyleToBlock(
            baseModel,
            blockPath,
            style,
            this.stylesToApply[style],
          );
        }
      } else {
        // add style
        await this.context.contentManipulator.applyStyleToBlock(
          baseModel,
          blockPath,
          style,
          this.stylesToApply[style],
        );
      }
    }
  }

  private applyStyleToTextData(
    range: Editor.Selection.JsonRange,
    baseModel: Editor.Data.Node.Model,
    blockData: Editor.Data.Node.Data,
    blockPath: Editor.Selection.Path,
    style: Editor.Edition.InlineStyles,
  ) {
    const baseData = baseModel.selectedData();

    if (!this.context.stylesManager || !this.context.contentManipulator || !baseData) {
      return false;
    }

    const stylesApplied = this.context.stylesManager.getStylesAppliedToContent(
      baseData,
      range.getCommonAncestorPath(),
      StylesUtils.STYLES,
      {
        includeInheritedStyles: true,
      },
    );

    let resultRange: Editor.Selection.JsonRange = range;

    if (StylesUtils.isAllowedInlineStyle(blockData.type, style)) {
      if (StylesUtils.isInlineExlusiveStyle(style)) {
        // exlusive styles
        const styleToRemove = StylesUtils.INLINE_EXCLUSIVE_STYLES[style];
        if (styleToRemove) {
          // undo to range
          resultRange = this.context.contentManipulator.removeStyleFromContent(
            baseModel,
            resultRange,
            styleToRemove,
          );
        }
      }

      if (StylesUtils.isInlineBooleanStyle(style)) {
        // boolean styles
        let valueToApply: boolean =
          this.stylesToApply[style] === true ||
          //@ts-expect-error intentional failsafe validation
          this.stylesToApply[style] === 'true';
        let valueToRemove: boolean = !valueToApply;

        // undo to range oposite value
        resultRange = this.context.contentManipulator.removeStyleFromContent(
          baseModel,
          resultRange,
          style,
          valueToRemove,
        );

        // only applys:
        // if there is a value applied and the value to apply is different
        // or if there is no value applied and the value to apply is true
        if (
          (stylesApplied[style] != null && stylesApplied[style] !== valueToApply) ||
          (stylesApplied[style] == null && valueToApply === true)
        ) {
          // apply to range
          resultRange = this.context.contentManipulator.applyStyleToContent(
            baseModel,
            resultRange,
            style,
            valueToApply,
          );
        }
      } else if (StylesUtils.isInlineSingleStateStyle(style)) {
        // single state styles

        if (this.stylesToApply[style] === true) {
          // apply to range
          resultRange = this.context.contentManipulator.applyStyleToContent(
            baseModel,
            resultRange,
            style,
            this.stylesToApply[style],
          );
        } else {
          // undo to range
          resultRange = this.context.contentManipulator.removeStyleFromContent(
            baseModel,
            resultRange,
            style,
          );
        }
      } else {
        // remove from range
        resultRange = this.context.contentManipulator.removeStyleFromContent(
          baseModel,
          resultRange,
          style,
          // this.stylesToApply[style],
        );

        // apply to range
        resultRange = this.context.contentManipulator.applyStyleToContent(
          baseModel,
          resultRange,
          style,
          this.stylesToApply[style],
        );
      }
    }

    range.updateRangePositions(resultRange.start, resultRange.end);
  }

  private async applyStylesToRange(ctx: Editor.Edition.ActionContext) {
    if (!this.context.DataManager || !this.context.stylesManager) {
      return false;
    }

    // normalize text selection
    SelectionFixer.normalizeTextSelection(
      ctx.range,
      {
        // suggestionMode: this.context.editionMode === 'SUGGESTIONS',
        forceTextAsWrap: true,
      },
      this.context.DataManager,
    );

    const blockRangesData = JsonRange.splitRangeByTypes(
      this.context.DataManager,
      ctx.range,
      [...StylesUtils.STYLABLE_BLOCKS],
      {
        sameContainerLevel: this.options.sameContainerLevel || false,
        useSelectedCells: this.options.useSelectedCells || true,
      },
    );

    for (let i = 0; i < blockRangesData.length; i++) {
      const range = blockRangesData[i].range;
      const blockData = blockRangesData[i].filteredData;
      const blockPath = blockRangesData[i].filteredDataPath;

      const baseModel = this.context.DataManager.nodes.getNodeModelById(range.start.b);

      // check if element is editable
      if (!baseModel || !this.context.DataManager.nodes.isNodeEditable(baseModel.id)) {
        continue;
      }

      let subStartPath: Editor.Selection.Path | undefined;
      if (range.start.p.length >= blockPath.length) {
        subStartPath = range.start.p.slice(blockPath.length);
      }

      let subEndPath: Editor.Selection.Path | undefined;
      if (range.end.p.length >= blockPath.length) {
        subEndPath = range.end.p.slice(blockPath.length);
      }

      if (subStartPath && subEndPath) {
        let isPathAtContentStart = NodeUtils.isPathAtContentStart(blockData, subStartPath);
        let isPathAtContentEnd = NodeUtils.isPathAtContentEnd(blockData, subEndPath);

        // WARN: pre process left indentation and special indentation ( blame MSWord )
        // TODO: this should move to import / export
        if (NodeUtils.isParagraphData(blockData)) {
          if (this.stylesToApply.leftIndentation != null) {
            const specialIndentType =
              this.stylesToApply.specialIndent || blockData.properties?.sp_ind?.t;
            const specialIndentValue =
              this.stylesToApply.specialIndentValue || blockData.properties?.sp_ind?.v;

            if (specialIndentType === 'h' && specialIndentValue != null) {
              this.stylesToApply.leftIndentation =
                Number(this.stylesToApply.leftIndentation) + Number(specialIndentValue);
            }
          } else if (
            this.stylesToApply.specialIndent === 'h' &&
            this.stylesToApply.specialIndentValue != null
          ) {
            const oldSpecialIndenttype = blockData.properties?.sp_ind?.t;
            const oldSpecialIndentValue = blockData.properties?.sp_ind?.v;

            let leftInd = blockData.properties?.ind?.l || 0;

            if (oldSpecialIndenttype === 'h' && oldSpecialIndentValue != null) {
              leftInd = leftInd - oldSpecialIndentValue;
            }

            this.stylesToApply.leftIndentation =
              leftInd + Number(this.stylesToApply.specialIndentValue);
          }
        }

        const styles = Object.typedKeys(this.stylesToApply);

        for (let i = 0; i < styles.length; i++) {
          const style = styles[i];
          if (StylesUtils.isAllowedInlineStyle(blockData.type, style)) {
            // Apply inline styles
            if (NodeUtils.isBlockTextData(blockData)) {
              if (isPathAtContentStart && isPathAtContentEnd) {
                await this.applyStyleToBlockData(baseModel, blockData, blockPath, style);
              }
              this.applyStyleToTextData(range, baseModel, blockData, blockPath, style);
            } else {
              await this.applyStyleToBlockData(baseModel, blockData, blockPath, style);
            }
          } else if (StylesUtils.isAllowedBlockStyle(blockData.type, style)) {
            // Apply exclusive block styles
            await this.applyStyleToBlockData(baseModel, blockData, blockPath, style);
          }
        }
      } else {
        // something is wrong
        Logger.captureException('ERROR: Invalid paths apply styles!!!');
      }

      // update range positions
      if (i === 0) {
        ctx.range.updateStartPosition(range.start);
      }

      if (i === blockRangesData.length - 1) {
        ctx.range.updateEndPosition(range.end);
      }
    }
  }

  protected async handleExec(...args: any[]) {
    if (this.context.debug) {
      Logger.trace('ApplyStyleCommand exec', this);
    }

    if (this.options.untrackedActionWarning && !this.askUserAboutThis()) {
      return;
    }

    this.buildActionContext(this.rangeToApply);

    if (!this.actionContext) {
      throw new Error('Context is not defined');
    }

    if (Object.keys(this.stylesToApply).length > 0) {
      await this.applyStylesToRange(this.actionContext);
    }

    if (this.options.useSelectedCells || this.options.collapseSelection === 'START') {
      this.actionContext.range.collapseToStart();
    }
    if (this.options.collapseSelection === 'END') {
      this.actionContext.range.collapseToEnd();
    }

    if (this.options.applySelection) {
      this.applySelection();
    }

    // force selection changed to update styles values
    // this.context.VisualizerManager?.selection.triggerSelectionChanged();

    if (this.options.createPatch) {
      this.createPatch();
    }
    // }
  }
}
